import { ReactComponent as OutlineChevronLeft } from "assets/svg/outline-chevron-left-16.svg";
import { ReactComponent as OutlineChevronRight } from "assets/svg/outline-chevron-right-16.svg";
import { useCalendarTime } from "components/Dashboard/DoctorDashboard/views/one-one-sessions/contexts/CalendarTimeContext";
import IconButton from "components/icon-button";
import { DAYS_OF_WEEK } from "constants/daysOfWeek";
import moment from "moment-timezone";
import React, { useCallback, useMemo } from "react";
import { PRO_CALENDAR_ANALYTICS_PAGE_NAME, track } from "utils/amplitude";
import getClasses from "utils/getClasses";
import useNormalizedEvents from "../../../monthly-view/hooks/use-normalized-events";
import styles from "./SingleMonth.module.scss";
import { useSwipeable } from "react-swipeable";

interface Props {}

const ANALYTICS_COMPONENT_NAME = "single-month-mobile";

const SingleMonth: React.FC<Props> = () => {
  const {
    currentCalendarTimezone,
    anchorTime,
    setAnchorTime,
    onChangeTimeRange,
  } = useCalendarTime();

  const normalizedEvents = useNormalizedEvents();

  const dayHasEvents = useCallback(
    (dayStart: number) => {
      const dayEnd = moment
        .tz(dayStart, currentCalendarTimezone)
        .endOf("day")
        .valueOf();
      return normalizedEvents.some(
        (event) =>
          new Date(event.startISO).valueOf() < dayEnd &&
          new Date(event.startISO).valueOf() > dayStart,
      );
    },
    [normalizedEvents, currentCalendarTimezone],
  );

  const onChangeDay = (dayStartValue: number) => {
    track(
      PRO_CALENDAR_ANALYTICS_PAGE_NAME,
      [ANALYTICS_COMPONENT_NAME, "day"],
      "click",
    );
    setAnchorTime(dayStartValue);
  };

  const calendarStartValue = useMemo(
    () =>
      moment
        .tz(
          moment.tz(anchorTime, currentCalendarTimezone).startOf("month"),
          currentCalendarTimezone,
        )
        .startOf("week")
        .valueOf(),
    [anchorTime, currentCalendarTimezone],
  );

  const monthStartValue = useMemo(
    () =>
      moment
        .tz(
          moment.tz(anchorTime, currentCalendarTimezone).startOf("month"),
          currentCalendarTimezone,
        )
        .valueOf(),
    [anchorTime, currentCalendarTimezone],
  );

  const monthEndValue = useMemo(
    () =>
      moment
        .tz(
          moment.tz(monthStartValue, currentCalendarTimezone).endOf("month"),
          currentCalendarTimezone,
        )
        .valueOf(),
    [monthStartValue, currentCalendarTimezone],
  );

  const selectedDayValue = useMemo(
    () =>
      moment.tz(anchorTime, currentCalendarTimezone).startOf("day").valueOf(),
    [anchorTime, currentCalendarTimezone],
  );

  const dateLabel = useMemo(
    () =>
      moment
        .tz(monthStartValue, currentCalendarTimezone)
        .startOf("month")
        .format("MMM YYYY"),
    [monthStartValue, currentCalendarTimezone],
  );

  const weeksInMonth = useMemo(() => {
    let weekStartValue = moment
      .tz(calendarStartValue, currentCalendarTimezone)
      .valueOf();

    const weeks = [] as number[];

    while (weekStartValue < monthEndValue) {
      weeks.push(weekStartValue);
      weekStartValue = moment
        .tz(weekStartValue, currentCalendarTimezone)
        .add(7, "day")
        .valueOf();
    }

    return weeks.map((weekStartValue) => ({
      weekStartValue,
      daysInWeek: new Array(7)
        .fill(0)
        .map((_, j) =>
          moment
            .tz(weekStartValue, currentCalendarTimezone)
            .add(j, "day")
            .valueOf(),
        ),
    }));
  }, [calendarStartValue, currentCalendarTimezone, monthEndValue]);

  const handlers = useSwipeable({
    onSwipedLeft: () => onChangeTimeRange(1),
    onSwipedRight: () => onChangeTimeRange(-1),
    delta: 40,
    swipeDuration: 500,
  });

  return (
    <div className={styles.container} {...handlers}>
      <table className={styles.calendar}>
        <thead className={styles["calendar-header"]}>
          <tr className={styles.controls}>
            <td>
              <div className={styles["cell-content"]}>
                <IconButton onClick={() => onChangeTimeRange(-1)}>
                  <OutlineChevronLeft />
                </IconButton>
              </div>
            </td>
            <td colSpan={5}>
              <div className={styles["date-label"]}>{dateLabel}</div>
            </td>
            <td>
              <div className={styles["cell-content"]}>
                <IconButton onClick={() => onChangeTimeRange(1)}>
                  <OutlineChevronRight />
                </IconButton>
              </div>
            </td>
          </tr>
          <tr>
            {DAYS_OF_WEEK.map((dayOfWeek) => (
              <th key={dayOfWeek} scope="col">
                <div className={styles["cell-content"]}>
                  {dayOfWeek.slice(0, 1).toUpperCase()}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeksInMonth.map(({ weekStartValue, daysInWeek }) => (
            <tr key={weekStartValue} className={styles.row}>
              {daysInWeek.map((dayValue) => (
                <td key={dayValue}>
                  <div
                    onClick={() => onChangeDay(dayValue)}
                    className={getClasses(
                      styles["cell-content"],
                      dayHasEvents(dayValue) && styles["has-events"],
                      dayValue === selectedDayValue && styles.selected,
                      dayValue < monthStartValue && styles.pastMonth,
                      dayValue > monthEndValue && styles.nextMonth,
                    )}
                  >
                    {moment.tz(dayValue, currentCalendarTimezone).date()}
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default SingleMonth;
